/**
 * Storage adapter for token and user data persistence
 * Auto-detects environment and uses appropriate storage:
 * - Browser: localStorage
 * - WeChat Mini Program: wx.setStorageSync/wx.getStorageSync
 * - Douyin Mini Program: `tt.setStorageSync` / `tt.getStorageSync`
 * - SSR/Node.js: No-op (no persistence)
 */

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

/**
 * Storage keys for auth data
 */
export const STORAGE_KEYS = {
  ACCESS_TOKEN: "amaster_access_token",
  REFRESH_TOKEN: "amaster_refresh_token",
  USER: "amaster_user",
} as const;

type MiniProgramStorageGlobal = {
  getStorageSync?(key: string): string;
  setStorageSync?(key: string, value: string): void;
  removeStorageSync?(key: string): void;
};

function resolveGlobalRuntime():
  | (typeof globalThis & {
      wx?: MiniProgramStorageGlobal;
      tt?: MiniProgramStorageGlobal;
    })
  | undefined {
  if (typeof globalThis !== "undefined") {
    return globalThis as typeof globalThis & {
      wx?: MiniProgramStorageGlobal;
      tt?: MiniProgramStorageGlobal;
    };
  }

  if (typeof self !== "undefined") {
    return self as typeof globalThis & {
      wx?: MiniProgramStorageGlobal;
      tt?: MiniProgramStorageGlobal;
    };
  }

  if (typeof window !== "undefined") {
    return window as typeof globalThis & {
      wx?: MiniProgramStorageGlobal;
      tt?: MiniProgramStorageGlobal;
    };
  }

  try {
    return Function("return this")() as typeof globalThis & {
      wx?: MiniProgramStorageGlobal;
      tt?: MiniProgramStorageGlobal;
    };
  } catch {
    return undefined;
  }
}

function getMiniProgramStorageGlobal(name: "wx" | "tt"): MiniProgramStorageGlobal | undefined {
  const runtime = resolveGlobalRuntime();

  return runtime?.[name];
}

/**
 * Detect current runtime environment
 */
function detectEnvironment(): "browser" | "wechat-miniprogram" | "douyin-miniprogram" | "node" {
  const wxRuntime = getMiniProgramStorageGlobal("wx");
  const ttRuntime = getMiniProgramStorageGlobal("tt");

  // WeChat Mini Program
  if (wxRuntime?.getStorageSync && wxRuntime?.setStorageSync && wxRuntime?.removeStorageSync) {
    return "wechat-miniprogram";
  }

  // Douyin Mini Program
  if (ttRuntime?.getStorageSync && ttRuntime?.setStorageSync && ttRuntime?.removeStorageSync) {
    return "douyin-miniprogram";
  }
  
  // Browser
  if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
    return "browser";
  }
  
  // Node.js/SSR
  return "node";
}

/**
 * Create a storage adapter that auto-detects environment
 * 
 * @returns Storage adapter optimized for current environment
 * 
 * @example
 * ```typescript
 * const storage = createStorageAdapter();
 * 
 * // Browser → uses localStorage
 * // WeChat Mini Program → uses wx.setStorageSync
 * // Douyin Mini Program -> uses tt.setStorageSync
 * // SSR/Node.js → uses no-op (no persistence)
 * ```
 */
export function createStorageAdapter(): StorageAdapter {
  const env = detectEnvironment();
  
  switch (env) {
    case "wechat-miniprogram":
      return createMiniProgramStorage(
        getMiniProgramStorageGlobal("wx") as Required<MiniProgramStorageGlobal>,
        "WeChat"
      );
    case "douyin-miniprogram":
      return createMiniProgramStorage(
        getMiniProgramStorageGlobal("tt") as Required<MiniProgramStorageGlobal>,
        "Douyin"
      );
    case "browser":
      return createBrowserStorage();
    case "node":
      return createNoOpStorage();
  }
}

/**
 * Create browser storage adapter (localStorage)
 */
function createBrowserStorage(): StorageAdapter {
  const storage = window.localStorage;

  return {
    getItem(key: string): string | null {
      try {
        return storage.getItem(key);
      } catch (error) {
        console.error("[AuthClient] Failed to get item from localStorage:", error);
        return null;
      }
    },

    setItem(key: string, value: string): void {
      try {
        storage.setItem(key, value);
      } catch (error) {
        console.error("[AuthClient] Failed to set item in localStorage:", error);
      }
    },

    removeItem(key: string): void {
      try {
        storage.removeItem(key);
      } catch (error) {
        console.error("[AuthClient] Failed to remove item from localStorage:", error);
      }
    },

    clear(): void {
      try {
        // Only clear auth-related keys, not all storage
        storage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        storage.removeItem(STORAGE_KEYS.USER);
      } catch (error) {
        console.error("[AuthClient] Failed to clear localStorage:", error);
      }
    },
  };
}

/**
 * Create Mini Program storage adapter
 * Uses platform.getStorageSync and platform.setStorageSync
 */
function createMiniProgramStorage(
  platform: {
    getStorageSync(key: string): string;
    setStorageSync(key: string, value: string): void;
    removeStorageSync(key: string): void;
  },
  platformName: "WeChat" | "Douyin"
): StorageAdapter {
  return {
    getItem(key: string): string | null {
      try {
        const value = platform.getStorageSync(key);
        return value || null;
      } catch (error) {
        console.error(`[AuthClient] Failed to get item from ${platformName} storage:`, error);
        return null;
      }
    },

    setItem(key: string, value: string): void {
      try {
        platform.setStorageSync(key, value);
      } catch (error) {
        console.error(`[AuthClient] Failed to set item in ${platformName} storage:`, error);
      }
    },

    removeItem(key: string): void {
      try {
        platform.removeStorageSync(key);
      } catch (error) {
        console.error(`[AuthClient] Failed to remove item from ${platformName} storage:`, error);
      }
    },

    clear(): void {
      try {
        // Only clear auth-related keys, not all storage
        platform.removeStorageSync(STORAGE_KEYS.ACCESS_TOKEN);
        platform.removeStorageSync(STORAGE_KEYS.REFRESH_TOKEN);
        platform.removeStorageSync(STORAGE_KEYS.USER);
      } catch (error) {
        console.error(`[AuthClient] Failed to clear ${platformName} storage:`, error);
      }
    },
  };
}

/**
 * In-memory storage adapter for SSR/Node.js/test environments
 * Data is stored in memory but not persisted across process restarts
 */
function createNoOpStorage(): StorageAdapter {
  const store = new Map<string, string>();
  
  return {
    getItem(key: string): string | null {
      return store.get(key) ?? null;
    },
    setItem(key: string, value: string): void {
      store.set(key, value);
    },
    removeItem(key: string): void {
      store.delete(key);
    },
    clear(): void {
      store.clear();
    },
  };
}
